// src/components/Sidebar.js

import React, { useState, useEffect } from 'react';
import ChatMessage from './ChatMessage.js';
import OptionBatchBubble from './OptionBatchBubble.js';
import { Button } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import HistoryIcon from '@mui/icons-material/History';
import IconButton from '@mui/material/IconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery, useTheme } from '@mui/material';
import { trackEvent } from '../services/analytics.js';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useRemix from '../hooks/useRemix.js';

export default function Sidebar({
  messages,
  uploadedImage,
  setUploadedImage,
  chatEndRef,
  input,
  setInput,
  sendMessage,
  isController,
  collabSessionActive,
  showParentHistory,
  parentMessages,
  loadingParentHistory,
  loadParentHistory,
  parentChatId,
  parentUsername,
  setShowParentHistory,
  isOwner,
  optionState,
  jumpToOption,
  setAcceptedId,
  appendOptionAsVersion,
  sending,
  awaitingOption
}) {
  const handleSend = () => {
    trackEvent('prompt_submitted', { subtype: 'iterative submission' });
    sendMessage(input, uploadedImage);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);

  const { username, chatId: prototypeId } = useParams();
  const location                          = useLocation();
  const navigate = useNavigate();
  const { remix, loading: remixLoading } = useRemix();

  // ──────────────────────────────────────────────────────────
  // Hide the REMIX button only once ‒ right after a fork.
  // We look for the flag injected by Header, then clear it.
  // ──────────────────────────────────────────────────────────
  const [justRemixed, setJustRemixed] = useState(
    !!location.state?.justRemixed
  );

  useEffect(() => {
    if (justRemixed) {
      // After first paint let the button come back
      const t = setTimeout(() => setJustRemixed(false), 400);
      return () => clearTimeout(t);
    }
  }, [justRemixed]);

  const handleRemix = async () => {
    trackEvent({
      action:   'click_remix',
      category: 'navigation',
      label:    'Remix Prototype'
    });
    try {
      const newId = await remix(prototypeId);
      navigate(
        `/${username}/${newId}`,
        { state: { showCongrats: true, sidebarVisible: true, justRemixed: true } }
      );
    } catch (err) {
      console.error('Remix failed:', err);
    }
  };

  // 1) situations where the whole input bar should disappear
  const inputHidden =
    (collabSessionActive && !isController) ||   // spectator in a session
    !isOwner;                                   // non-owner

  // 2) situations where we only need to disable it
  const inputDisabled =
    sending ||          // waiting for model
    awaitingOption;     // options shown but not accepted yet

  // helper: any pending options on a phone?
  const hasPendingOptions =
    optionState && optionState.status === 'pending' && isMobile;

  // …but only the owner may actually pick:
  const hasPendingOptionsOwner = hasPendingOptions && isOwner;

  // helper: are we on a phone while the model is still replying?
  const isGeneratingMobile = isMobile && sending;
  const hideHistoryToggle  = isGeneratingMobile || hasPendingOptions;

  /* ------------------------------------------------------------
   * Show REMIX?
   *  – don’t while options are pending
   *  – don’t while LLM is generating
   *  – don’t during the tiny “justRemixed” window
   * ------------------------------------------------------------ */
  const canRemix =
    !awaitingOption &&
    !isGeneratingMobile &&
    !remixLoading &&
    !justRemixed;

  // which option id is currently “selected” in the picker?
  const [pickedId, setPickedId] = useState(null);
  // keep it in sync when optionState changes
  useEffect(() => {
    if (optionState?.hoveredId) setPickedId(optionState.hoveredId);
  }, [optionState]);

  // Listen for open/close events from Header
  useEffect(() => {
    const onOpen = () => setIsVisible(true);
    const onClose = () => setIsVisible(false);
    window.addEventListener('openMobileSidebar', onOpen);
    window.addEventListener('closeMobileSidebar', onClose);
    return () => {
      window.removeEventListener('openMobileSidebar', onOpen);
      window.removeEventListener('closeMobileSidebar', onClose);
    };
  }, []);

  const toggleChat = () => setIsVisible((v) => !v);

  // ─── HELPER: render a single message or OptionBatchBubble ─────────────────
  const renderMessage = (msg) => {
    // If this AI message carries the 3‐option metadata, show OptionBatchBubble
    if (msg.metadata?.isOptionBatch) {
      return (
        <OptionBatchBubble
          key={msg.key}
          data={msg.metadata}
          optionState={optionState}
          jumpToOption={jumpToOption}
          setAcceptedId={setAcceptedId}
          appendOptionAsVersion={appendOptionAsVersion}
          isOwner={isOwner}
        />
      );
    }
    // Otherwise, a normal chat‐message bubble
    return (
      <ChatMessage
        key={msg.key}
        sender={msg.sender}
        text={msg.text}
      />
    );
  };

  // ─── Non-owner on mobile: toggle entire chat (history only) ────────────────
  if (!isOwner && isMobile) {
    return (
      <>
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'left',
            justifyContent: 'space-between',
            padding: '8px',
            background: 'none',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
          }}
        >
          {/* Hide the history-toggle while choosing an option           */}
          {!isVisible && !hideHistoryToggle && (
            <button
              onClick={toggleChat}
              style={{
                padding: '8px 20px',
                background: '#000',
                color: '#fff',
                border: 'none',
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
              }}
            >
              <ChatIcon fontSize="small" style={{ marginRight: 4 }} /> Show History
            </button>
          )}

          {!inputHidden && !awaitingOption && !isGeneratingMobile ? (
            <>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && input.trim()) handleSend();
                }}
                placeholder={
                  awaitingOption
                    ? 'Pick an option to continue'
                    : 'What should we add or change?'
                }
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
                disabled={inputDisabled}
              />
              <button
                className="send-btn"
                onClick={() => input.trim() && handleSend()}
                style={{
                  marginLeft: '8px',
                  padding: '8px 12px',
                  background: 'black',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                disabled={!input.trim() || inputDisabled}
              >
                Send
              </button>
            </>
          ) : (canRemix) ? (
            <Button
              startIcon={<CallSplitIcon />}
              onClick={handleRemix}
              disabled={remixLoading}
              sx={{
                marginLeft: 'auto',
                padding: '16px 20px',
                fontSize: '16px',
                backgroundImage:
                  'linear-gradient(to right top, #1771f7, #0062f6, #0051f4, #003ff1, #1226eb)',
                color: 'white',
                borderRadius: '25px',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(0,100,255,0.2)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {remixLoading ? 'Remixing…' : 'REMIX'}
            </Button>
          ) : null }
        </div>

        <div className={`sidebar-panel mobile-sidebar ${isVisible ? 'visible' : ''}`}>
          {/* Chat history area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {parentChatId && !showParentHistory && (
              <div style={{ padding: '8px 16px', borderBottom: '1px solid #eee' }}>
                <Button
                  startIcon={<HistoryIcon />}
                  onClick={loadParentHistory}
                  disabled={loadingParentHistory}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  {loadingParentHistory ? 'Loading...' : 'See History'}
                </Button>
              </div>
            )}
            {showParentHistory &&
              parentMessages.map((msg) => (
                <React.Fragment key={msg.key}>
                  <ChatMessage sender={msg.sender} text={msg.text} />
                </React.Fragment>
              ))}
            {showParentHistory && (
              <div
                style={{
                  padding: '8px 16px',
                  borderBottom: '1px solid #eee',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '0.9em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span>Remix started here</span>
                <IconButton
                  size="small"
                  onClick={() => setShowParentHistory(false)}
                  title="Hide history"
                >
                  <ExpandLessIcon fontSize="small" />
                </IconButton>
              </div>
            )}
            {messages.map((msg) => (
              <React.Fragment key={msg.key}>{renderMessage(msg)}</React.Fragment>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Hide button over history */}
          {isVisible && (
            <button
              className="chat-toggle-button"
              onClick={toggleChat}
              style={{ bottom: '20px', zIndex: 1001 }}
            >
              Hide History <CloseIcon />
            </button>
          )}
        </div>
        {/* ─── Floating “pick an option” bar (read-only) ─────────── */}
        {hasPendingOptions && (
          <div
            className="option-picker-bar"
            style={{
              position:   'fixed',
              bottom:     16,
              left:       '50%',
              transform:  'translateX(-50%)',
              background: '#fff',
              border:     '1px solid #ccc',
              borderRadius: 24,
              padding:    '8px 12px',
              boxShadow:  '0 2px 8px rgba(0,0,0,0.15)',
              display:    'flex',
              gap:        8,
              zIndex:     1001,
            }}
          >
            <select
              value={pickedId ?? optionState.options[0].id}
              onChange={e => {
                const id  = +e.target.value;
                setPickedId(id);
                const idx = optionState.options.findIndex(o => o.id === id);
                if (idx >= 0) jumpToOption(idx);     // preview only
              }}
              style={{ fontSize: 14, padding: '6px 10px', borderRadius: 6 }}
            >
              {optionState.options.map((o, i) => (
                <option key={o.id} value={o.id}>
                  Option {String.fromCharCode(65 + i)}
                </option>
              ))}
            </select>

            {/* Disabled for non-owners */}
            <button
              disabled                     // <- key line
              style={{
                padding:      '6px 14px',
                borderRadius: 6,
                border:       'none',
                background:   '#ddd',
                color:        '#888',
                fontSize:     14,
                cursor:       'default'
              }}
            >
              Pick
            </button>
          </div>
        )}
      </>
    );
  }

  // ─── Owner on mobile: always show input, toggle history only ──────────────
  if (isOwner && isMobile) {
    return (
      <>
        {!isVisible && !hideHistoryToggle && (
          <button
            className="chat-toggle-button"
            style={{ bottom: 'calc(60px + 16px)', zIndex: 1001 }}
            onClick={toggleChat}
          >
            <ChatIcon /> Show Prompt History
          </button>
        )}

        {/* History panel toggled separately */}
        {isVisible && (
          <div className="sidebar-panel mobile-sidebar visible">
            <button
              className="chat-toggle-button"
              style={{ bottom: 'calc(60px + 16px)', zIndex: 1001 }}
              onClick={toggleChat}
            >
              Hide History <CloseIcon />
            </button>
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                padding: '8px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {parentChatId && !showParentHistory && (
                <div style={{ padding: '8px 16px', borderBottom: '1px solid #eee' }}>
                  <Button
                    startIcon={<HistoryIcon />}
                    onClick={loadParentHistory}
                    disabled={loadingParentHistory}
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    {loadingParentHistory ? 'Loading...' : 'See History'}
                  </Button>
                </div>
              )}
              {showParentHistory &&
                parentMessages.map((msg) => (
                  <React.Fragment key={msg.key}>
                    <ChatMessage sender={msg.sender} text={msg.text} />
                  </React.Fragment>
                ))}
              {showParentHistory && (
                <div
                  style={{
                    padding: '8px 16px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '0.9em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <span>Remix started here</span>
                  <IconButton
                    size="small"
                    onClick={() => setShowParentHistory(false)}
                    title="Hide history"
                  >
                    <ExpandLessIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              {messages.map((msg) => (
                <React.Fragment key={msg.key}>{renderMessage(msg)}</React.Fragment>
              ))}
              <div ref={chatEndRef} />
            </div>
          </div>
        )}

        {/* Input/send always visible for owner, unless choosing options */}
        {/* While options are pending we *replace* the input bar with the
            floating picker — so skip the whole input/remix block      */}
        {!inputHidden && !hasPendingOptions && !isGeneratingMobile ? (
          <div
            style={{
              display: 'flex',
              padding: '8px',
              borderTop: '1px solid #ddd',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              background: '#fff',
              boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
              zIndex: 1000,
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && input.trim()) handleSend();
              }}
              placeholder={
                awaitingOption
                  ? 'Pick an option to continue'
                  : 'What should we add or change?'
              }
              style={{
                flex: 1,
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
              disabled={inputDisabled}
            />
            <button
              className="send-btn"
              onClick={() => input.trim() && handleSend()}
              style={{
                marginLeft:'8px',
                padding:'8px 12px',
                background: inputDisabled ? '#d3d3d3' : 'black',
                color:      inputDisabled ? '#666'    : 'white',
                border:'none',
                borderRadius:'4px',
                cursor: inputDisabled ? 'default' : 'pointer',
              }}
              disabled={!input.trim() || inputDisabled}
            >
              Send
            </button>
          </div>
        ) : (canRemix && !hasPendingOptions) ? (
          <div style={{ padding: '8px', borderTop: '1px solid #ddd' }}>
            <Button fullWidth startIcon={<CallSplitIcon />} onClick={handleRemix}>
              REMIX
            </Button>
          </div>
        ) : null }

        {/* ─── Floating “pick an option” bar ────────────────────────────── */}
        {hasPendingOptions && (
          <div
            className="option-picker-bar"
            style={{
              position: 'fixed',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: 24,
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',     /* stack label above controls */
              alignItems: 'center',
              gap: 8,
              zIndex: 1001,
            }}
          >
            {/* instruction */}
            <span style={{
              fontSize: 13,
              fontStyle: 'italic',
              color: '#666',
              marginBottom: '0.5em'
            }}>
              Pick an option to continue
            </span>
          
            {/* controls */}
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={pickedId ?? optionState.options[0].id}
                onChange={e => {
                  const id = +e.target.value;
                  setPickedId(id);
                  const idx = optionState.options.findIndex(o => o.id === id);
                  if (idx >= 0) jumpToOption(idx);
                }}
                style={{ fontSize: 14, padding: '6px 10px', borderRadius: 6 }}
              >
                {optionState.options.map((o, i) => (
                  <option key={o.id} value={o.id}>
                    Option {String.fromCharCode(65 + i)}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  const opt = optionState.options.find(o => o.id === pickedId);
                  if (opt) appendOptionAsVersion(opt.idx);
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#000',
                  color: '#fff',
                  fontSize: 14,
                }}
              >
                Pick
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── Desktop or tablet: default sidebar ────────────────────────────────────
  return (
    <div className="sidebar-panel">
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {parentChatId && !showParentHistory && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid #eee' }}>
            <Button
              startIcon={<HistoryIcon />}
              onClick={loadParentHistory}
              disabled={loadingParentHistory}
              variant="outlined"
              size="small"
              fullWidth
            >
              {loadingParentHistory ? 'Loading...' : 'See History'}
            </Button>
          </div>
        )}
        {showParentHistory &&
          parentMessages.map((msg) => (
            <React.Fragment key={msg.key}>
              <ChatMessage sender={msg.sender} text={msg.text} />
            </React.Fragment>
          ))}
        {showParentHistory && (
          <div
            style={{
              padding: '8px 16px',
              borderBottom: '1px solid #eee',
              textAlign: 'center',
              color: '#666',
              fontSize: '0.9em',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <span>Remix started here</span>
            <IconButton
              size="small"
              onClick={() => setShowParentHistory(false)}
              title="Hide history"
            >
              <ExpandLessIcon fontSize="small" />
            </IconButton>
          </div>
        )}
        {messages.map((msg) => (
          <React.Fragment key={msg.key}>{renderMessage(msg)}</React.Fragment>
        ))}
        <div ref={chatEndRef} />
      </div>
      {!inputHidden ? (
        <div style={{ display: 'flex', padding: '8px', borderTop: '1px solid #ddd' }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && input.trim()) handleSend();
            }}
            placeholder={
              awaitingOption
                ? 'Pick an option to continue'
                : 'What should we add or change?'
            }
            style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            disabled={inputDisabled}
          />
          <button
            className="send-btn"
            onClick={() => input.trim() && handleSend()}
            style={{
              marginLeft:'8px',
              padding:'8px 12px',
              background: inputDisabled ? '#d3d3d3' : 'black',
              color:      inputDisabled ? '#666'    : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: inputDisabled ? 'default' : 'pointer',
            }}
            disabled={!input.trim() || inputDisabled}
          >
            Send
          </button>
        </div>
      ) : canRemix ? (
        <div style={{ padding: '8px', borderTop: '1px solid #ddd' }}>
          <Button
            fullWidth
            startIcon={<CallSplitIcon />}
            onClick={handleRemix}
            disabled={remixLoading}
            sx={{
              padding: '16px 20px',
              fontSize: '16px',
              backgroundImage:
                'linear-gradient(to right top, #1771f7, #0062f6, #0051f4, #003ff1, #1226eb)',
              color: 'white',
              borderRadius: '25px',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(0, 100, 255, 0.2)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            {remixLoading ? 'Remixing…' : 'REMIX'}
          </Button>
        </div>
      ) : null }
    </div>
  );
}
